<template>
  <div class="dash-card birthdays-widget">
    <div class="dash-card-header">
      <h3 class="dash-card-title">
        <span
          class="dash-card-title-icon"
          style="background: #fff0f6; color: #eb2f96"
        >
          <a-icon type="gift" />
        </span>
        {{ $t("accueil.dashboard.birthdays") }}
      </h3>
    </div>

    <div class="dash-card-body">
      <a-input
        v-model="search"
        :placeholder="$t('accueil.dashboard.searchByName')"
        allow-clear
        class="birthdays-search"
      >
        <a-icon slot="prefix" type="search" />
      </a-input>

      <div v-if="filteredBirthdays.length" class="birthdays-list dash-scroll">
        <div
          v-for="person in filteredBirthdays"
          :key="`${person.type}-${person._id}`"
          class="birthday-item"
          :class="{ 'is-clickable': isStudent(person) }"
          @click="openPerson(person)"
        >
          <img
            v-if="photoUrl(person)"
            class="birthday-avatar"
            :src="photoUrl(person)"
            alt=""
          />
          <span
            v-else
            class="dash-avatar-initials birthday-avatar"
          >
            {{ initialsFor(person) }}
          </span>

          <div class="birthday-info">
            <div class="birthday-name-row">
              <span class="birthday-name">
                {{ person.firstName }} {{ person.lastName }}
              </span>
              <span
                class="birthday-type"
                :class="
                  isStudent(person)
                    ? 'birthday-type--student'
                    : 'birthday-type--staff'
                "
              >
                {{
                  isStudent(person)
                    ? $t("accueil.dashboard.student")
                    : $t("accueil.dashboard.staff")
                }}
              </span>
            </div>

            <div class="birthday-meta">
              <template v-if="isStudent(person)">
                <a-icon type="read" />
                <span class="birthday-class" dir="auto">{{
                  className(person)
                }}</span>
                <template v-if="levelLabel(person)">
                  <span class="birthday-sep">·</span>
                  <span class="birthday-level" dir="ltr">{{
                    levelLabel(person)
                  }}</span>
                </template>
              </template>
              <template v-else>
                <a-icon type="idcard" />
                <span>{{ staffRole(person) }}</span>
              </template>
            </div>
          </div>

          <div
            class="birthday-when"
            :class="{ 'is-today': daysFromToday(person) === 0 }"
          >
            <a-icon type="calendar" />
            <span>{{ birthdayLabel(person) }}</span>
          </div>
        </div>
      </div>

      <div v-else class="dash-empty-state birthdays-empty">
        <div class="dash-empty-icon">
          <a-icon type="smile" />
        </div>
        <div>{{ $t("accueil.dashboard.noBirthdaysToday") }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";

export default {
  name: "BirthdaysWidget",
  props: {
    birthdays: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: "",
    };
  },
  computed: {
    ...mapState(["settings"]),
    filteredBirthdays() {
      const list = !this.search
        ? [...this.birthdays]
        : this.birthdays.filter((p) =>
            `${p.firstName} ${p.lastName}`
              .toLowerCase()
              .includes(this.search.toLowerCase())
          );

      return list.sort((a, b) => {
        const dayA = a.birthDay != null ? a.birthDay : this.dayOfMonth(a);
        const dayB = b.birthDay != null ? b.birthDay : this.dayOfMonth(b);
        return dayA - dayB;
      });
    },
  },
  methods: {
    isStudent(person) {
      return person.type === "student";
    },
    birthDateOf(person) {
      return person.BirthDate || person.birthDate;
    },
    dayOfMonth(person) {
      const birth = moment(this.birthDateOf(person));
      return birth.isValid() ? birth.date() : 99;
    },
    photoUrl(person) {
      const photo = person && person.photo;
      if (!photo || typeof photo !== "string") return null;
      if (photo.startsWith("http")) return photo;
      const base = this.settings && this.settings.resourcesUrl;
      if (!base) return null;
      return base.replace("FILE_PATH", photo);
    },
    className(person) {
      return (person.classRoom && person.classRoom.name) || "—";
    },
    levelLabel(person) {
      const level = person.classRoom && person.classRoom.level;
      if (level === null || level === undefined || level === "") return "";
      const key = `niveau.niveau${level}`;
      const label = this.$t(key);
      return label === key ? "" : label;
    },
    staffRole(person) {
      return (
        (person.position && person.position.name) ||
        this.$t("accueil.dashboard.staff")
      );
    },
    openPerson(person) {
      if (!this.isStudent(person) || !person._id) return;
      const path = `/eleve/${person._id}`;
      if (this.$route.path !== path) {
        this.$router.push({ path });
      }
    },
    daysFromToday(person) {
      const birth = moment(this.birthDateOf(person));
      if (!birth.isValid()) return null;

      const today = moment().startOf("day");
      const thisYear = birth.clone().year(today.year()).startOf("day");
      return thisYear.diff(today, "days");
    },
    birthdayLabel(person) {
      const days = this.daysFromToday(person);
      if (days === null) return "";
      if (days === 0) return this.$t("accueil.dashboard.birthdayToday");
      if (days === 1) return this.$t("accueil.dashboard.birthdayTomorrow");
      if (days > 1) {
        return this.$t("accueil.dashboard.birthdayInDays", { days });
      }

      const birth = moment(this.birthDateOf(person));
      const thisYear = birth.clone().year(moment().year());
      return this.$t("accueil.dashboard.birthdayOnDate", {
        date: thisYear.format("D MMM"),
      });
    },
    initialsFor(person) {
      const a = (person.firstName || "").trim().charAt(0);
      const b = (person.lastName || "").trim().charAt(0);
      return `${a}${b}`.toUpperCase() || "?";
    },
  },
};
</script>

<style lang="scss" scoped>
.birthdays-widget {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.dash-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.birthdays-search {
  margin-bottom: 14px;
  flex-shrink: 0;
}

.birthdays-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 160px;
  max-height: 320px;
  overflow-y: auto;
}

.birthdays-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 160px;
}

.birthday-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 10px;
  transition: background 0.18s ease;

  &:hover {
    background: #fafafa;
  }

  &.is-clickable {
    cursor: pointer;
  }
}

.birthday-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  font-size: 14px;
  background: #fff0f6;
  color: #eb2f96;
}

.birthday-info {
  flex: 1;
  min-width: 0;
}

.birthday-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 3px;
}

.birthday-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #262626;
}

.birthday-type {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;

  &--student {
    background: #e6f7ff;
    color: #1890ff;
  }

  &--staff {
    background: #f9f0ff;
    color: #722ed1;
  }
}

.birthday-meta {
  font-size: 0.72rem;
  color: #8c8c8c;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.birthday-class {
  unicode-bidi: isolate;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
}

.birthday-sep {
  flex-shrink: 0;
  opacity: 0.7;
}

.birthday-level {
  flex-shrink: 0;
  unicode-bidi: isolate;
  direction: ltr;
}

.birthday-when {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f5f5f5;
  color: #595959;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;

  &.is-today {
    background: #fff0f6;
    color: #eb2f96;
  }
}

[data-kit-theme="dark"] {
  .birthday-item:hover {
    background: #141322;
  }

  .birthday-name {
    color: #aeaee0;
  }

  .birthday-type--student {
    background: rgba(24, 144, 255, 0.18);
  }

  .birthday-type--staff {
    background: rgba(114, 46, 209, 0.18);
  }

  .birthday-when {
    background: #232135;
    color: #aeaee0;

    &.is-today {
      background: rgba(235, 47, 150, 0.18);
      color: #eb2f96;
    }
  }
}
</style>
